import type { UseHomeMarketplaceDataResult } from "../../hooks/useHomeMarketplaceData";
import type {
  DashboardSnapshot,
  FinanceReadinessPreviewTier,
} from "../../lib/dashboard";
import { deriveHighestVerificationTier } from "../../lib/dashboard";
import {
  deriveHomeHeroState,
  type HomeHeroState,
  type HomeHeroStateKind,
} from "../../lib/home-hero-state";
import type { OfferForBuyer } from "../../lib/marketplace-offers-api";
import {
  deriveMarketplaceCardState,
  type MarketplaceCardState,
} from "../../lib/marketplace-status";
import type { WaitlistPosition } from "../../lib/waitlist-api";

/**
 * Plan 435 M3 (mfh-tier-unlock-row, mfh-next-move). Same mapping
 * `components/dashboard/profile/tier-derivation.ts`'s `deriveBadgeTier`
 * uses, inlined here rather than imported because that function's
 * signature demands the FULL `DashboardSnapshot` and this module only
 * carries the narrow `Pick` `computeHomeSettledInputs` already takes.
 * `"t1"`/`"t2"`/`"t3"` is the SAME string shape `OfferBuyerTier` uses, so
 * this is the one place that bridges "achievement tier" to "offer tier".
 */
function offerTierFromVerificationTier(
  tier: FinanceReadinessPreviewTier,
): "t1" | "t2" | "t3" {
  switch (tier) {
    case "bank-verified":
    case "lender-approved":
      return "t3";
    case "document-backed":
      return "t2";
    case "self-reported":
    default:
      return "t1";
  }
}

/**
 * Plan 435 M1/M2 (mfh-home-settle / mfh-hero-state). The settled-inputs
 * shape `deriveHomeHeroState` consumes — computed ONCE, after
 * `useHomeMarketplaceData` reports `settled: true`, so the first real paint
 * never flips (decision: "the hero settles before it paints").
 *
 * `cardState` is `deriveMarketplaceCardState`'s output, reused verbatim
 * (tech-spec Concepts "Hero state" / "Connection state" — never re-derived).
 *
 * Honesty traps pinned here, not in `lib/home-hero-state.ts` (that module
 * has no concept of `publishedAt` or browse `access` — only the booleans a
 * caller hands it):
 *   - `isLive` reads `snapshot.portalDiscoverable` ONLY. `publishedAt`
 *     stays set after unpublish and must never feed this.
 *   - `targetUnresolved` reads browse `access === "target_unresolved"` ONLY
 *     (plan 454, decision 519) and maps to the `no_target` hero kind; it is
 *     never folded into `areaLaunched`.
 *   - `areaLaunched` reads browse `access !== "area_not_launched"` ONLY.
 *     Waitlist membership never decides launch state — a buyer can be
 *     unpublished in an unlaunched metro with no waitlist row at all.
 *   - A failed browse read (`browse === null`) degrades `areaLaunched` to
 *     `true` and `agentCount` to 0 — never blocks, never fabricates a
 *     waitlist state the server never actually returned.
 */
export interface HomeSettledInputs {
  cardState: MarketplaceCardState;
  heroState: HomeHeroState;
  connection: UseHomeMarketplaceDataResult["connection"];
  pendingRequest: UseHomeMarketplaceDataResult["pendingRequest"];
  browse: UseHomeMarketplaceDataResult["browse"];
  inbox: UseHomeMarketplaceDataResult["inbox"];
  agentCount: number;
  offersCount: number;
  /** Target area label fed to `deriveHomeHeroState` — threaded through for
   *  copy that needs it outside the hero state itself (e.g. the "Join the
   *  waitlist" success toast). */
  area: string;
  /** The buyer's own achievement tier, offer-tier-shaped ("t1"/"t2"/"t3") —
   *  feeds `TierUnlockRow`'s `currentTier` (never enable the T3 case) and
   *  `AgentPreview`'s unpublished-copy tier. */
  currentTier: "t1" | "t2" | "t3";
  verificationTier: FinanceReadinessPreviewTier;
  /** The first browse agent's `pro_user_id`, or null with no agents — the
   *  ONE candidate `TierUnlockRow`'s single extra request is drawn from. */
  firstAgentProUserId: string | null;
  /** The top made-for-you offer, or null — `NextMove`'s offers-expiry
   *  branch reads only this one. */
  topOffer: OfferForBuyer | null;
}

export function computeHomeSettledInputs(
  data: UseHomeMarketplaceDataResult,
  snapshot: Pick<
    DashboardSnapshot,
    "portalDiscoverable" | "targetArea" | "financialInputs"
  >,
  waitlistPosition: WaitlistPosition | null,
): HomeSettledInputs {
  const cardState = deriveMarketplaceCardState({
    connection: data.connection,
    offerCounts: {
      madeForYou: data.inbox?.made_for_you.length ?? 0,
      availableToYou: data.inbox?.available_to_you.length ?? 0,
    },
    pendingRequest: data.pendingRequest,
  });

  const isLive = snapshot.portalDiscoverable === true;
  const hasTarget = snapshot.targetArea.trim().length > 0;
  // A failed/not-yet-loaded browse read degrades to "launched" — a browse
  // outage must never masquerade as an honest "not launched here" claim.
  const areaLaunched = data.browse ? data.browse.access !== "area_not_launched" : true;
  // Plan 454 (decision 519): "target_unresolved" is its own server answer, NOT
  // "not launched" - it must reach the hero as the no_target kind with the
  // confirm-your-target copy, never as a waitlist claim about an area.
  const targetUnresolved = data.browse?.access === "target_unresolved";
  const area = data.browse?.area_label ?? snapshot.targetArea;

  const heroState = deriveHomeHeroState({
    cardState,
    isLive,
    hasTarget,
    areaLaunched,
    targetUnresolved,
    waitlistPosition,
    offersCount: data.offersCount,
    agentCount: data.agentCount,
    area,
    tier: data.pendingRequest?.buyer_tier,
    proDisplayName: data.pendingRequest?.pro_display_name,
    expiresAt: data.pendingRequest?.expires_at,
    checkinPending: data.connection?.checkin?.pending,
  });

  const verificationTier = deriveHighestVerificationTier(snapshot);

  return {
    cardState,
    heroState,
    connection: data.connection,
    pendingRequest: data.pendingRequest,
    browse: data.browse,
    inbox: data.inbox,
    agentCount: data.agentCount,
    offersCount: data.offersCount,
    area,
    currentTier: offerTierFromVerificationTier(verificationTier),
    verificationTier,
    firstAgentProUserId: data.browse?.agents[0]?.identity?.pro_user_id ?? null,
    topOffer: data.inbox?.made_for_you[0] ?? null,
  };
}

/**
 * Plan 435 M3/M4 (mfh-next-move carried instruction: "implement as a single
 * visibility function that M4 will extend, not scattered conditionals";
 * M4's mfh-lifecycle-visibility extends it in place here, never a second
 * visibility helper).
 *
 * `showRequestCard`/`showConnectedStack` are the M4 additions — `waiting_
 * agent` keeps `showAgents` ("Other agents serving {area}" stays visible
 * alongside the request) and gains `RequestCard`; `connected` replaces the
 * whole open market with `ConnectedStack` (roadmap decision "Connected
 * replaces the market"). `no_target` now shows the next-move block (the
 * roadmap's explicit carve-out: "no_target shows the target next move"),
 * unlike `waiting_agent`/`connected`/`finalizing*`, which supply their own
 * next-move surface (the request card's own CTA, `ConnectedStack`'s right
 * column, or nothing at all) and must never double up with a second,
 * generic `NextMove` block.
 *
 * `hasRecoveryWindow` is an orthogonal override (roadmap M4 bullet 5 /
 * mfh-recovery-card): an open recovery window hides every open-market
 * block regardless of the hero kind underneath it — the recovery card (and
 * Prepare) is the whole body, mirroring the `finalizing*` shape, because
 * the buyer's attention belongs on the one choice in front of them, not on
 * a market that is mid-transition. It is a second parameter rather than a
 * new `HomeHeroStateKind` because the hero's AMBER styling is already a
 * generic `HomeHero` capability (see `amber` prop) layered on top of
 * whatever kind the connection status machine resolves — recovery is not
 * itself a connection state.
 */
export interface HomeBlockVisibility {
  showOffers: boolean;
  showAgents: boolean;
  showUnlock: boolean;
  showNextMove: boolean;
  /** `waiting_agent` only. Not touched by a recovery window — design §5.3's
   *  recovery row hides only offers/agents (see `RECOVERY_HIDDEN` below). */
  showRequestCard: boolean;
  /** `connected` only. Not touched by a recovery window. */
  showConnectedStack: boolean;
}

/**
 * M4 iteration-2 review fix (finding 4): design §5.3's recovery row hides
 * ONLY offers and agents (plus the tier-unlock row, which is structurally
 * empty without agents anyway) — it does NOT blank the whole body the way
 * `finalizing*` does. Everything else keeps rendering per the underlying
 * hero kind: the connected stack and its right column stay, next move stays
 * wherever that kind already shows it, and Prepare (rendered unconditionally
 * by `HomeView`, outside this visibility function) always stays. Applied as
 * a narrow override on top of the base per-kind visibility rather than a
 * second full-override map, so a recovery window can never accidentally
 * hide a block the base state wants shown.
 */
const RECOVERY_HIDDEN: Pick<HomeBlockVisibility, "showOffers" | "showAgents" | "showUnlock"> = {
  showOffers: false,
  showAgents: false,
  showUnlock: false,
};

export function computeHomeBlockVisibility(
  kind: HomeHeroStateKind,
  hasRecoveryWindow = false,
): HomeBlockVisibility {
  const base = computeBaseVisibility(kind);
  return hasRecoveryWindow ? { ...base, ...RECOVERY_HIDDEN } : base;
}

function computeBaseVisibility(kind: HomeHeroStateKind): HomeBlockVisibility {
  switch (kind) {
    case "unpublished_agents":
    case "unpublished_none":
    case "live_offers":
    case "live_quiet":
      return {
        showOffers: true,
        showAgents: true,
        showUnlock: true,
        showNextMove: true,
        showRequestCard: false,
        showConnectedStack: false,
      };
    case "area_not_launched":
      // `AgentPreview` renders `BrowseWaitlistState` in this state (never
      // hidden — the buyer still gets a join/share surface); offers and
      // the tier-unlock row are structurally empty here anyway (no agents
      // means no inbox and no ladder), but hidden explicitly so a future
      // backend quirk can't surface either.
      return {
        showOffers: false,
        showAgents: true,
        showUnlock: false,
        showNextMove: true,
        showRequestCard: false,
        showConnectedStack: false,
      };
    case "no_target":
      return {
        showOffers: false,
        showAgents: false,
        showUnlock: false,
        showNextMove: true,
        showRequestCard: false,
        showConnectedStack: false,
      };
    case "waiting_agent":
      return {
        showOffers: false,
        showAgents: true,
        showUnlock: false,
        showNextMove: false,
        showRequestCard: true,
        showConnectedStack: false,
      };
    case "connected":
      return {
        showOffers: false,
        showAgents: false,
        showUnlock: false,
        showNextMove: false,
        showRequestCard: false,
        showConnectedStack: true,
      };
    case "finalizing":
    case "finalizing_escape":
      return {
        showOffers: false,
        showAgents: false,
        showUnlock: false,
        showNextMove: false,
        showRequestCard: false,
        showConnectedStack: false,
      };
  }
}
